import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { isValidTimezone } from '@gogrov/shared'
import type { AppSetting, Client, PipelineJob, User, UserClient } from '@/types/database'

/** Every write below is gated by RLS admin-override policies, not by AdminGuard. */

const CLIENT_LIST_KEY = ['admin', 'client-list']
const clientKey = (clientId: string) => ['admin', 'client', clientId]

export type ClientRole = UserClient['role']

export interface ClientInput {
  slug: string
  display_name: string
  city_state: string
  state: string
  timezone: string
  accent_color: string
}

export interface ClientUpdateInput {
  display_name: string
  city_state: string
  state: string
  timezone: string
  accent_color: string
  active: boolean
}

export interface ClientMember {
  membership: UserClient
  user: User | null
}

/** Supabase errors are plain objects; surface the message rather than "[object Object]". */
function fail(context: string, error: { message?: string } | null): never {
  throw new Error(`${context}: ${error?.message ?? 'unknown error'}`)
}

function useInvalidateClient() {
  const qc = useQueryClient()
  return (clientId?: string) => {
    void qc.invalidateQueries({ queryKey: CLIENT_LIST_KEY })
    if (clientId) void qc.invalidateQueries({ queryKey: clientKey(clientId) })
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Clients
// ─────────────────────────────────────────────────────────────────────────────

export function useCreateClient() {
  const invalidate = useInvalidateClient()

  return useMutation({
    mutationFn: async (input: ClientInput & { places: string[] }) => {
      if (!isValidTimezone(input.timezone)) {
        throw new Error(`"${input.timezone}" is not a valid IANA timezone.`)
      }

      const { data: created, error } = await supabase
        .from('clients')
        .insert({
          slug: input.slug.trim(),
          display_name: input.display_name.trim(),
          city_state: input.city_state.trim(),
          state: input.state.trim().toUpperCase(),
          timezone: input.timezone.trim(),
          accent_color: input.accent_color,
        })
        .select('*')
        .single()

      if (error || !created) fail('Could not create client', error)
      const client = created as Client

      // Seed rows are what make the client usable by the pipeline. If either
      // insert fails the client is unusable, so roll it back rather than
      // leaving a half-provisioned record behind (no transaction over PostgREST).
      try {
        const places = input.places.map((p) => p.trim()).filter(Boolean)
        for (const place of places) {
          // add_region() rather than a direct insert, so provisioning obeys the
          // same plan cap a client would hit. The new client has no
          // onboarded_at yet, so these are free.
          const { error: placeErr } = await supabase.rpc('add_region', {
            p_client_id: client.id, p_name: place, p_scope: 'city',
          })
          if (placeErr) fail('Could not seed coverage areas', placeErr)
        }
        // Topics are chosen in onboarding, as a mix that sums to 100 within
        // the plan's cap, so nothing is seeded here.
      } catch (e) {
        await supabase.from('clients').delete().eq('id', client.id)
        throw e
      }

      return client
    },
    onSuccess: (client) => invalidate(client.id),
  })
}

export function useUpdateClient() {
  const invalidate = useInvalidateClient()

  return useMutation({
    mutationFn: async ({ clientId, input }: { clientId: string; input: ClientUpdateInput }) => {
      if (!isValidTimezone(input.timezone)) {
        throw new Error(`"${input.timezone}" is not a valid IANA timezone.`)
      }

      const { data, error } = await supabase
        .from('clients')
        .update({
          display_name: input.display_name.trim(),
          city_state: input.city_state.trim(),
          state: input.state.trim().toUpperCase(),
          timezone: input.timezone.trim(),
          accent_color: input.accent_color,
          active: input.active,
        })
        .eq('id', clientId)
        .select('*')
        .single()

      if (error || !data) fail('Could not update client', error)
      return data as Client
    },
    onSuccess: (client) => invalidate(client.id),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Coverage (regions)
// ─────────────────────────────────────────────────────────────────────────────

export function useAddRegion() {
  const invalidate = useInvalidateClient()

  return useMutation({
    mutationFn: async (
      { clientId, name, scope }: { clientId: string; name: string; scope?: string }
    ) => {
      const trimmed = name.trim()
      if (!trimmed) throw new Error('Enter a place name.')

      // Written through add_region() so the plan cap holds for admins too.
      const { error } = await supabase.rpc('add_region', {
        p_client_id: clientId, p_name: trimmed, p_scope: scope ?? 'city',
      })
      if (error) fail('Could not add coverage area', error)
      return clientId
    },
    onSuccess: (clientId) => invalidate(clientId),
  })
}

export function useToggleRegion() {
  const invalidate = useInvalidateClient()

  return useMutation({
    mutationFn: async ({ id, active, clientId }: { id: string; active: boolean; clientId: string }) => {
      const { error } = await supabase.from('regions').update({ active }).eq('id', id)
      if (error) fail('Could not update coverage area', error)
      return clientId
    },
    onSuccess: (clientId) => invalidate(clientId),
  })
}

export function useRemoveRegion() {
  const invalidate = useInvalidateClient()

  return useMutation({
    mutationFn: async ({ id, clientId }: { id: string; clientId: string }) => {
      const { error } = await supabase.from('regions').delete().eq('id', id)
      if (error) fail('Could not remove coverage area', error)
      return clientId
    },
    onSuccess: (clientId) => invalidate(clientId),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Topic weights
// ─────────────────────────────────────────────────────────────────────────────

export function useSaveTopicWeights() {
  const invalidate = useInvalidateClient()

  return useMutation({
    mutationFn: async ({ clientId, weights }: { clientId: string; weights: Record<string, number> }) => {
      const rows = Object.entries(weights).map(([topic, weight]) => ({
        client_id: clientId,
        topic,
        // DB enforces `check (weight between 0 and 1)` — clamp instead of 400ing.
        weight: Math.min(1, Math.max(0, Number(weight.toFixed(2)))),
      }))

      const { error } = await supabase
        .from('topic_weights')
        .upsert(rows, { onConflict: 'client_id,topic' })
      if (error) fail('Could not save topic weights', error)
      return clientId
    },
    onSuccess: (clientId) => invalidate(clientId),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Members (user_clients)
// ─────────────────────────────────────────────────────────────────────────────

export function useClientMembers(clientId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'client-members', clientId],
    enabled: !!clientId,
    queryFn: async (): Promise<ClientMember[]> => {
      const { data, error } = await supabase
        .from('user_clients')
        .select('*')
        .eq('client_id', clientId!)
        .order('created_at')
      if (error) fail('Could not load members', error)

      const memberships = (data ?? []) as UserClient[]
      if (memberships.length === 0) return []

      const { data: users, error: usersErr } = await supabase
        .from('users')
        .select('*')
        .in('id', memberships.map((m) => m.user_id))
      if (usersErr) fail('Could not load member profiles', usersErr)

      const byId = new Map((users ?? []).map((u) => [(u as User).id, u as User]))
      return memberships.map((membership) => ({
        membership,
        user: byId.get(membership.user_id) ?? null,
      }))
    },
  })
}

function useInvalidateMembers() {
  const qc = useQueryClient()
  return (clientId: string) => {
    void qc.invalidateQueries({ queryKey: ['admin', 'client-members', clientId] })
  }
}

export function useAddClientMember() {
  const invalidateMembers = useInvalidateMembers()

  return useMutation({
    mutationFn: async ({ clientId, identifier, role }: {
      clientId: string
      identifier: string
      role: ClientRole
    }) => {
      const value = identifier.trim()
      if (!value) throw new Error('Enter a user id or email.')

      const column = value.includes('@') ? 'email' : 'id'
      const { data: user, error } = await supabase
        .from('users')
        .select('id')
        .eq(column, column === 'email' ? value.toLowerCase() : value)
        .maybeSingle()
      if (error) fail('Could not look up user', error)
      if (!user) throw new Error(`No user found for "${value}". They must sign up first.`)

      const { error: insertErr } = await supabase
        .from('user_clients')
        .insert({ client_id: clientId, user_id: (user as { id: string }).id, role })
      if (insertErr) fail('Could not add member', insertErr)

      return clientId
    },
    onSuccess: (clientId) => invalidateMembers(clientId),
  })
}

export function useUpdateClientMemberRole() {
  const invalidateMembers = useInvalidateMembers()

  return useMutation({
    mutationFn: async ({ id, role, clientId }: { id: string; role: ClientRole; clientId: string }) => {
      const { error } = await supabase.from('user_clients').update({ role }).eq('id', id)
      if (error) fail('Could not update role', error)
      return clientId
    },
    onSuccess: (clientId) => invalidateMembers(clientId),
  })
}

export function useRemoveClientMember() {
  const invalidateMembers = useInvalidateMembers()

  return useMutation({
    mutationFn: async ({ id, clientId }: { id: string; clientId: string }) => {
      const { error } = await supabase.from('user_clients').delete().eq('id', id)
      if (error) fail('Could not remove member', error)
      return clientId
    },
    onSuccess: (clientId) => invalidateMembers(clientId),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// app_settings (operator-entered API keys)
// ─────────────────────────────────────────────────────────────────────────────

/** Keys the pipeline reads when the matching env var isn't set. Order is display order. */
export const MANAGED_SETTING_KEYS = [
  'ANTHROPIC_API_KEY',
  'ANTHROPIC_MODEL',
  'SERP_API_KEY',
  'APNS_KEY_P8',
  'APNS_KEY_ID',
  'APNS_TEAM_ID',
  'APNS_BUNDLE_ID',
  'APNS_PRODUCTION',
  'FCM_SERVICE_ACCOUNT_JSON',
  'NOTIFY_HOUR',
  // The newsletter sends from GoGrov's own Resend account (migration 20240049).
  'RESEND_API_KEY',
  'RESEND_WEBHOOK_SECRET',
  'NEWSLETTER_FROM_DOMAIN',
  'PUBLIC_APP_URL',
] as const

export type ManagedSettingKey = (typeof MANAGED_SETTING_KEYS)[number]

/**
 * Displayable values: model/bundle ids, team and key ids, and send-time config
 * are non-sensitive identifiers. Everything else in MANAGED_SETTING_KEYS is
 * treated as a credential and never leaves the server — notably APNS_KEY_P8
 * (the signing key itself) and FCM_SERVICE_ACCOUNT_JSON, which are secret
 * regardless of the ids alongside them.
 */
export const NON_SECRET_SETTING_KEYS: readonly string[] = [
  'ANTHROPIC_MODEL',
  'APNS_KEY_ID',
  'APNS_TEAM_ID',
  'APNS_BUNDLE_ID',
  'APNS_PRODUCTION',
  'NOTIFY_HOUR',
  'NEWSLETTER_FROM_DOMAIN',
  'PUBLIC_APP_URL',
]

/** A setting as the UI sees it: secret values are dropped before they reach state. */
export interface SettingView {
  key: string
  description: string | null
  updated_at: string
  isSet: boolean
  /** Populated only for NON_SECRET_SETTING_KEYS. */
  value: string | null
}

export function useAppSettings() {
  return useQuery({
    queryKey: ['admin', 'app-settings'],
    queryFn: async (): Promise<SettingView[]> => {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .in('key', MANAGED_SETTING_KEYS as unknown as string[])
      if (error) fail('Could not load settings', error)

      // Secrets are discarded here rather than in the component so they never
      // land in the React Query cache or a devtools snapshot.
      return ((data ?? []) as AppSetting[]).map((s) => ({
        key: s.key,
        description: s.description,
        updated_at: s.updated_at,
        isSet: s.value !== null && s.value !== '',
        value: NON_SECRET_SETTING_KEYS.includes(s.key) ? s.value : null,
      }))
    },
    gcTime: 0,
  })
}

export function useSaveAppSetting() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string | null }) => {
      const { error } = await supabase
        .from('app_settings')
        .update({ value })
        .eq('key', key)
      if (error) fail(`Could not save ${key}`, error)
      return key
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'app-settings'] })
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Pipeline jobs
// ─────────────────────────────────────────────────────────────────────────────

export function usePipelineJobs(clientId: string | undefined, limit = 20) {
  return useQuery({
    queryKey: ['admin', 'pipeline-jobs', clientId, limit],
    enabled: !!clientId,
    queryFn: async (): Promise<PipelineJob[]> => {
      const { data, error } = await supabase
        .from('pipeline_jobs')
        .select('*')
        .eq('client_id', clientId!)
        .order('created_at', { ascending: false })
        .limit(limit)
      if (error) fail('Could not load pipeline jobs', error)
      return (data ?? []) as PipelineJob[]
    },
    refetchInterval: 15_000,
  })
}

